<template>
  <div class="ArticleCard">
    <NuxtLink
      :to="url"
      :aria-label="title"
      no-prefetch
    >
      <div
        class="ArticleCard__media"
        :class="`ArticleCard__media--${width}`"
      >
        <!-- 2/4 width card image -->
        <picture
          v-if="media && media.isImage && media.src && width === '2/4'"
          class="ArticleCard__bg-img"
        >
          <!-- Portrait images -->
          <source
            :srcset="portraitSrcset"
            media="(max-width: 649px)"
            :sizes="sizes"
          />

          <!-- Landscape images -->
          <source
            :srcset="landscapeSrcset"
            media="(min-width: 650px)"
            :sizes="sizes"
          />

          <img
            :src="media.src"
            :srcset="srcSets"
            :sizes="sizes"
            :alt="media.alt"
            loading="lazy"
          />
        </picture>

        <!-- 1/4 width card image -->
        <img
          v-else-if="media && media.isImage && media.src && width === '1/4'"
          :src="media.src"
          :srcset="portraitSrcset"
          :sizes="sizes"
          :alt="media.alt"
          class="ArticleCard__bg-img"
          loading="lazy"
        />

        <!-- Video -->
        <div :class="$b('media-video')">
          <BaseVideo
            v-if="media.isVideo && media.video?.muxPlaybackId"
            :video="media"
            :preview-start-time="0"
            :loop="true"
            :bg-video="true"
          />
        </div>

        <VisionTag
          v-if="isNew"
          class="ArticleCard__tag"
          size="small"
          text="NEW"
        />
        <VisionIcon
          icon="arrow"
          colour="white"
          :class="$b('arrow')"
        />
        <div :class="$b('overlay')" />
        <div :class="$b('gradient-overlay')" />
      </div>
    </NuxtLink>

    <div class="ArticleCard__content">
      <VisionLink
        :class="[$b('pre-title'), 't-body-sm']"
        :to="preTitleUrl"
      >
        {{ preTitle }}
      </VisionLink>

      <VisionLink
        :class="[$b('title'), 't-h3']"
        :to="url"
      >
        {{ title }}
      </VisionLink>

      <VisionLink
        v-if="subtitle"
        :class="[$b('subtitle'), 't-h4']"
        :to="url"
      >
        {{ subtitle }}
      </VisionLink>

      <BaseCopy
        v-if="showReadTime"
        :class="$b('read-time')"
        size="body-sm"
      >
        {{ readTime }} {{ readTime > 1 ? 'mins' : 'min' }} read
      </BaseCopy>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ArticleCard',

  props: {
    media: {
      type: Object,
      required: true,
    },

    title: {
      type: String,
      required: true,
    },

    subtitle: {
      type: String,
      default: null,
    },

    preTitle: {
      type: String,
      required: true,
    },

    preTitleUrl: {
      type: String,
      required: true,
    },

    publishedAt: {
      type: String,
      default: null,
    },

    url: {
      type: String,
      required: true,
    },

    isNew: {
      type: Boolean,
      default: false,
    },

    width: {
      type: String,
      default: '1/4',
      validator: (width) => ['1/4', '2/4'].includes(width),
    },

    wordCount: {
      type: Number,
      default: null,
    },
  },

  setup() {
    const { imgixSize } = useImgix()

    return { imgixSize }
  },

  computed: {
    sizes() {
      if (this.width === '2/4') {
        return `
          (min-width: 1150px) calc(1 / 2 * (100vw - (150px + 64px)),
          (min-width: 800px) calc(2 / 3 * (100vw - (150px + 64px)),
          (min-width: 650px) calc(100vw - 45px),
          100vw
        `
      }

      // 1/4 width
      return `
        (min-width: 1150px) calc(1 / 4 * (100vw - (150px + 64px))),
        (min-width: 800px) calc(1 / 3 * (100vw - (150px + 64px))),
        calc(100vw - 45px)
      `
    },

    srcSets() {
      return this.width === '2/4' ? this.landscapeSrcset : this.portraitSrcset
    },

    landscapeSrcset() {
      const { src, focalPoint } = this.media
      return `
        ${this.imgixSize(src, { size: 'landscape_2xl', focalPoint })} 1600w,
        ${this.imgixSize(src, { size: 'landscape_xl', focalPoint })} 1200w,
        ${this.imgixSize(src, { size: 'landscape_md', focalPoint })} 800w,
        ${this.imgixSize(src, { size: 'landscape_sm', focalPoint })} 600w,
      `
    },

    portraitSrcset() {
      const { src, focalPoint } = this.media
      return `
        ${this.imgixSize(src, { size: 'portrait_xxl', focalPoint })} 1500w,
        ${this.imgixSize(src, { size: 'portrait_xl', focalPoint })} 1200w,
        ${this.imgixSize(src, { size: 'portrait_lg', focalPoint })} 800w,
        ${this.imgixSize(src, { size: 'portrait_md', focalPoint })} 600w,
        ${this.imgixSize(src, { size: 'portrait_sm', focalPoint })} 450w,
        ${this.imgixSize(src, { size: 'portrait_xs', focalPoint })} 320w,
        ${this.imgixSize(src, { size: 'portrait_xxs', focalPoint })} 196w
      `
    },

    readTime() {
      const wordsPerMinute = 200
      const words = this.wordCount
      return Math.ceil(words / wordsPerMinute)
    },

    showReadTime() {
      const categories = ['Film']
      return this.wordCount && !categories.includes(this.preTitle)
    },
  },
}
</script>

<style lang="postcss">
.ArticleCard__media {
  @apply relative overflow-hidden h-0 sm:mb-sm;
  border-radius: 10px;
  /* Set the height to 0 but use percentage padding to get a portrait aspect ratio. */
  padding-bottom: calc(405 / 304 * 100%);
  margin-bottom: 20px;
  isolation: isolate;

  &--2\/4 {
    @screen smMax {
      margin-left: calc(0px - var(--outer-gutter));
      margin-right: calc(0px - var(--outer-gutter));
      border-radius: 0;
    }

    @screen sm {
      /* Set the height to 0 but use percentage padding to get a landscape aspect ratio. Taking into account gutter spaces. */
      padding-bottom: calc((405 / 608 * 100%) - (64px / 3));
    }
  }
}

.ArticleCard__bg-img {
  @apply absolute h-full w-full object-cover;

  & img {
    @apply h-full w-full object-cover;
  }
}

.ArticleCard__media-video {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  aspect-ratio: 12/16;

  .ArticleCard__media--2\/4 & {
    aspect-ratio: 4/2.9;

    @screen mdMax {
      aspect-ratio: 12/16;
      border-radius: 0;
    }
  }
}

.ArticleCard__overlay {
  @apply absolute bg-black
    w-full h-full
    top-0 left-0
    opacity-0
    transition-opacity duration-300 ease-out;

  .ArticleCard:hover & {
    @apply lg:opacity-20;
  }
}

.ArticleCard__gradient-overlay {
  @apply absolute
    w-full h-full
    top-0 left-0
    z-10
    opacity-0 transition-opacity duration-300 ease-out;

  background-image: var(--gradient-default);

  .ArticleCard:hover & {
    @apply lg:opacity-60;
  }
}

.ArticleCard__arrow {
  @apply absolute bottom-sm left-sm sm:bottom-md sm:left-md
    z-20
    opacity-0
    transition-all delay-100 ease-in-out;

  transform: translate3d(-50px, 2px, 0);

  .ArticleCard:hover & {
    @screen lg {
      @apply opacity-100;
      transform: translate3d(0, 2px, 0);
    }
  }
}

.ArticleCard__tag {
  @apply absolute top-xs right-xs;
}

.ArticleCard__pre-title {
  @apply block text-off-black opacity-40 uppercase font-semibold tracking-wider
    transition-opacity duration-200 ease-in-out
    mb-xxxs sm:mb-xxs;

  &:hover {
    @apply opacity-100 text-off-black;
  }
}

.ArticleCard__read-time {
  @apply text-off-black opacity-60 mt-xxxs;
}

.ArticleCard__title {
  @apply block;

  /* Set the max width of the text relative to its font size. */
  max-width: calc(416 / 24 * 1em);

  .ArticleCard:hover & {
    @apply text-grey-dark;
  }
}

.ArticleCard__subtitle {
  @apply block text-off-black opacity-60 mt-xxxs;
}
</style>
